import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Title,
    Tooltip,
} from 'chart.js';
import { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { FaAngleDown, FaAngleUp, FaBuilding, FaHome, FaSearch, FaUserNurse, FaUsers } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import { useTenant } from '../context/TenantContext';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import TableSkeleton from './TableSkeleton';
import { getPageCache, setPageCache } from '../utils/pageCache';
import { formatCarePlanId, formatQuotationId } from '../utils/displayIds';
import { HOUR_FIELDS, sumServicePlanLineItems } from '../utils/staffDemandHours';
import { STAFF_DEMAND_CACHE_KEY, applyStaffDemandPayload, normalizeStaffDemandPayload } from '../utils/prefetchStaffDemand';
import { useStaffTypes } from '../hooks/useStaffTypes';
import StaffTypeAbbr from './StaffTypeAbbr';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const MODIFIERS = {
    statutory: 0.88,
    adminSNENA: 0.82,
    adminCW: 0.99,
    efficiency: 0.85,
    shiftHours: 8,
    shiftsPerPeriod: 12,
};

const STAFF_MIN = { sn: 1, ena: 2, cw: 4 };

// ── Styles ──────────────────────────────────────────────────────────────────
const COLORS = {
    dark: '#131926',
    darkMid: '#1e2d40',
    darkLight: '#253347',
    aqua: '#55fff4',
    cream: '#fefae7',
    grey: '#d1d2dc',
    snDay: '#3b82f6',
    snNight: '#93c5fd',
    enaDay: '#8b5cf6',
    enaNight: '#c4b5fd',
    cwDay: '#10b981',
    cwNight: '#6ee7b7',
    amber: '#f59e0b',
    textMuted: '#6b7280',
};

const thBase = {
    padding: '11px 10px',
    color: '#356167',
    fontWeight: 600,
    fontSize: '0.76rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    background: 'inherit',
    border: 'none',
    whiteSpace: 'nowrap',
};

// ── Pure calculation (outside component to avoid re-creation) ────────────────
// One row per care plan / customer. Prefers server-precomputed servicePlanHours;
// falls back to CarePlanDetail-aligned line-item math.
function calcTotalsPerCustomer(carePlansData) {
    const totalsMap = {};

    carePlansData.forEach(cp => {
        const customerId = cp._customerId || cp.customer_id;
        const customerName = cp._customerFullName || cp.customer_name;
        if (!customerId && !customerName) return;

        const key = customerId != null ? String(customerId) : customerName;
        if (!totalsMap[key]) {
            totalsMap[key] = {
                customer_name: customerName,
                customer_id: customerId || null,
                CarePlanID: cp.care_plan_id || cp.CarePlanID || 'N/A',
                sn_hours_day: 0,
                sn_hours_night: 0,
                ena_hours_day: 0,
                ena_hours_night: 0,
                cw_hours_day: 0,
                cw_hours_night: 0,
                discount: cp.discount || 0,
            };
        }

        const t = totalsMap[key];
        const hours = cp.servicePlanHours
            || sumServicePlanLineItems(cp.lineItems || cp.line_items || []);
        HOUR_FIELDS.forEach((field) => {
            t[field] += parseFloat(hours[field]) || 0;
        });
    });

    return Object.values(totalsMap);
}

// ── Component ────────────────────────────────────────────────────────────────
function StaffDemandReport() {
    // allCarePlans are enriched with `lineItems` and `_customerId` after fetching
    const [allCarePlans, setAllCarePlans] = useState([]);
    const [allCustomers, setAllCustomers] = useState([]); // fetched with include_sites=true
    const [tenantSites, setTenantSites] = useState([]);   // dropdown: sites for current tenant
    const [quotationsByCarePlanId, setQuotationsByCarePlanId] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [selectedSiteId, setSelectedSiteId] = useState('');
    const [recipientSearch, setRecipientSearch] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'customer_name', direction: 'ascending' });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    const { currentTenant } = useTenant();
    const { getAbbreviation, getDisplayName } = useStaffTypes();

    // Responsive breakpoint
    useEffect(() => {
        const handler = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handler);
        return () => window.removeEventListener('resize', handler);
    }, []);

    // Reset filters when tenant changes
    useEffect(() => {
        setSelectedSiteId('');
        setRecipientSearch('');
    }, [currentTenant?.id]);

    // Fetch all data via bulk endpoint (or pageCache warmed after login)
    useEffect(() => {
        if (!currentTenant?.id) return;
        const tenantId = currentTenant.id;
        const cacheKey = STAFF_DEMAND_CACHE_KEY(tenantId);

        const setters = {
            setAllCarePlans,
            setQuotationsByCarePlanId,
            setAllCustomers,
            setTenantSites,
        };

        const initializeData = async (background = false) => {
            if (!background) setIsLoading(true);
            try {
                const res = await fetchWithAuth(
                    `${API_BASE_URL}/staff-demand-report?tenant_id=${encodeURIComponent(tenantId)}`
                );
                if (!res.ok) {
                    throw new Error(`staff-demand-report failed: ${res.status}`);
                }
                const data = await res.json();
                const normalized = normalizeStaffDemandPayload(data);

                applyStaffDemandPayload(normalized, setters);
                setPageCache(cacheKey, normalized);
            } catch (e) {
                console.error('Error loading staff demand report:', e);
            } finally {
                setIsLoading(false);
            }
        };

        // Stale-while-revalidate: show cached report instantly, then refresh silently.
        const cached = getPageCache(cacheKey);
        if (cached) {
            applyStaffDemandPayload(cached, setters);
            setIsLoading(false);
            initializeData(true);
        } else {
            initializeData();
        }
    }, [currentTenant?.id]);

    // ── Derived data ────────────────────────────────────────────────────────

    // customer_id → Set of site_ids (from `sites` array on each customer)
    const customerSiteMap = useMemo(() => {
        const map = {};
        allCustomers.forEach(c => {
            if (!c.customer_id) return;
            const siteIds = new Set();
            (c.sites || []).forEach(s => {
                if (String(s.tenant_id) === String(currentTenant?.id)) {
                    siteIds.add(String(s.site_id));
                }
            });
            if (c.site_id) siteIds.add(String(c.site_id));
            map[String(c.customer_id)] = siteIds;
        });
        return map;
    }, [allCustomers, currentTenant?.id]);

    // tenant_site_id → site_id lookup, built from customer sites data (which carries both keys)
    const tenantSiteToSiteId = useMemo(() => {
        const map = {};
        allCustomers.forEach(c => {
            (c.sites || []).forEach(s => {
                if (s.tenant_site_id && s.site_id) {
                    map[String(s.tenant_site_id)] = String(s.site_id);
                }
            });
        });
        return map;
    }, [allCustomers]);

    // Filter priority:
    //  1. Care plan's own tenant_site_id (most specific — the plan was explicitly created for that site)
    //  2. Customer's site membership (fallback for older plans that have no tenant_site_id)
    const filteredCarePlans = useMemo(() => {
        if (!selectedSiteId) return allCarePlans;
        return allCarePlans.filter(cp => {
            const cpTenantSiteId = cp.tenant_site_id || cp.TenantSiteID;
            if (cpTenantSiteId) {
                const mappedSiteId = tenantSiteToSiteId[String(cpTenantSiteId)];
                if (mappedSiteId) return mappedSiteId === String(selectedSiteId);
            }
            // Fall back to customer's site membership
            const custId = cp._customerId;
            if (custId == null) return false;
            return (customerSiteMap[String(custId)] || new Set()).has(String(selectedSiteId));
        });
    }, [allCarePlans, selectedSiteId, customerSiteMap, tenantSiteToSiteId]);

    const totalsPerCustomer = useMemo(
        () => calcTotalsPerCustomer(filteredCarePlans),
        [filteredCarePlans]
    );

    const quotationTotalsPerCustomer = useMemo(() => {
        const byCustomer = {};
        filteredCarePlans.forEach(cp => {
            const cpId = cp.care_plan_id || cp.CarePlanID;
            const quotData = quotationsByCarePlanId[cpId]
                || quotationsByCarePlanId[String(cpId)];
            if (!quotData) return;

            const customerId = cp._customerId;
            const customerName = cp._customerFullName || cp.customer_name;
            const key = customerId != null ? String(customerId) : customerName;
            if (!byCustomer[key]) {
                byCustomer[key] = {
                    customer_name: customerName,
                    customer_id: customerId || null,
                    quotation_id: quotData.quotation_id,
                    date_created: quotData.date_created,
                    sn_hours_day: 0, sn_hours_night: 0,
                    ena_hours_day: 0, ena_hours_night: 0,
                    cw_hours_day: 0, cw_hours_night: 0,
                };
            }
            const q = byCustomer[key];
            q.sn_hours_day    += quotData.sn_hours_day    || 0;
            q.sn_hours_night  += quotData.sn_hours_night  || 0;
            q.ena_hours_day   += quotData.ena_hours_day   || 0;
            q.ena_hours_night += quotData.ena_hours_night || 0;
            q.cw_hours_day    += quotData.cw_hours_day    || 0;
            q.cw_hours_night  += quotData.cw_hours_night  || 0;
        });
        return Object.values(byCustomer);
    }, [filteredCarePlans, quotationsByCarePlanId]);

    const sortedTotals = useMemo(() => {
        const items = [...totalsPerCustomer];
        if (!sortConfig.key) return items;
        items.sort((a, b) => {
            let av = a[sortConfig.key];
            let bv = b[sortConfig.key];
            if (sortConfig.key === 'customer_name') {
                av = (av || '').toLowerCase();
                bv = (bv || '').toLowerCase();
            } else {
                av = parseFloat(av) || 0;
                bv = parseFloat(bv) || 0;
            }
            if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (av > bv) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });
        return items;
    }, [totalsPerCustomer, sortConfig]);

    const displayTotals = useMemo(() => {
        if (!recipientSearch.trim()) return sortedTotals;
        const s = recipientSearch.toLowerCase();
        return sortedTotals.filter(c => c.customer_name.toLowerCase().includes(s));
    }, [sortedTotals, recipientSearch]);

    const summaryTotals = useMemo(() => ({
        snDay:    totalsPerCustomer.reduce((s, c) => s + (c.sn_hours_day    || 0), 0),
        snNight:  totalsPerCustomer.reduce((s, c) => s + (c.sn_hours_night  || 0), 0),
        enaDay:   totalsPerCustomer.reduce((s, c) => s + (c.ena_hours_day   || 0), 0),
        enaNight: totalsPerCustomer.reduce((s, c) => s + (c.ena_hours_night || 0), 0),
        cwDay:    totalsPerCustomer.reduce((s, c) => s + (c.cw_hours_day    || 0), 0),
        cwNight:  totalsPerCustomer.reduce((s, c) => s + (c.cw_hours_night  || 0), 0),
    }), [totalsPerCustomer]);

    const staffRows = useMemo(() => {
        const { snDay, snNight, enaDay, enaNight, cwDay, cwNight } = summaryTotals;
        const { statutory, adminSNENA, adminCW, efficiency, shiftHours, shiftsPerPeriod } = MODIFIERS;

        const calcRequired = (h, adminMod) =>
            h / shiftHours / shiftsPerPeriod / statutory / adminMod / efficiency;

        return [
            { label: `${getAbbreviation('SN')} Day`,    color: COLORS.snDay,    hours: snDay,    calc: calcRequired(snDay,    adminSNENA), min: STAFF_MIN.sn  },
            { label: `${getAbbreviation('SN')} Night`,  color: COLORS.snNight,  hours: snNight,  calc: calcRequired(snNight,  adminSNENA), min: STAFF_MIN.sn  },
            { label: `${getAbbreviation('ENA')} Day`,   color: COLORS.enaDay,   hours: enaDay,   calc: calcRequired(enaDay,   adminSNENA), min: STAFF_MIN.ena },
            { label: `${getAbbreviation('ENA')} Night`, color: COLORS.enaNight, hours: enaNight, calc: calcRequired(enaNight, adminSNENA), min: STAFF_MIN.ena },
            { label: `${getAbbreviation('CW')} Day`,    color: COLORS.cwDay,    hours: cwDay,    calc: calcRequired(cwDay,    adminCW),    min: STAFF_MIN.cw  },
            { label: `${getAbbreviation('CW')} Night`,  color: COLORS.cwNight,  hours: cwNight,  calc: calcRequired(cwNight,  adminCW),    min: STAFF_MIN.cw  },
        ];
    }, [summaryTotals]);

    const chartData = useMemo(() => ({
        labels: staffRows.map(r => r.label),
        datasets: [{
            label: 'Hours / week',
            data: staffRows.map(r => r.hours),
            backgroundColor: staffRows.map(r => r.color),
            borderRadius: 5,
            borderSkipped: false,
        }],
    }), [staffRows]);

    // ── Helpers ─────────────────────────────────────────────────────────────

    const requestSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'ascending' ? 'descending' : 'ascending',
        }));
    };

    const sortIcon = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ? <FaAngleUp size={11} /> : <FaAngleDown size={11} />;
    };

    const hoursSum = (obj, keys) => keys.reduce((s, k) => s + (obj?.[k] || 0), 0);
    const HOUR_KEYS = HOUR_FIELDS;

    // ── Unified page styling (matches Care Recipients / Activities / Price Items) ──
    const pageWrap = {
        background: 'var(--theme-page-bg)',
        minHeight: '100vh',
        padding: isMobile ? '16px' : '24px 28px 40px',
        fontFamily: "var(--secondary-font, 'Roboto', sans-serif)",
    };
    const heroStyle = {
        background: 'var(--theme-surface)',
        border: '1px solid var(--theme-border)',
        borderRadius: 12,
        padding: isMobile ? '18px 16px' : '22px 24px',
        marginBottom: 20,
        boxShadow: '0 1px 2px var(--theme-shadow)',
    };
    const heroTitle = { color: 'var(--theme-text)', margin: 0, fontSize: isMobile ? '1.35rem' : '1.5rem', fontWeight: 700, letterSpacing: '-0.02em' };
    const heroSubtitle = { color: 'var(--theme-text-secondary)', margin: '4px 0 0', fontSize: '0.9rem' };
    const cardStyle = {
        background: 'var(--theme-surface)',
        border: '1px solid var(--theme-border)',
        borderRadius: 12,
        boxShadow: '0 1px 2px var(--theme-shadow)',
    };
    const filterFieldStyle = { display: 'flex', alignItems: 'center', gap: 8, background: 'var(--theme-input-bg)', border: '1px solid var(--theme-input-border)', borderRadius: 8, padding: '8px 12px', height: 40, flex: isMobile ? 1 : undefined };
    const filterInputStyle = { background: 'transparent', border: 'none', color: 'var(--theme-text-body)', fontSize: '0.88rem', outline: 'none' };

    if (isLoading) {
        return (
            <div style={pageWrap}>
                <div style={heroStyle}>
                    <h2 style={heroTitle}>Staff Demand Report</h2>
                    <p style={heroSubtitle}>
                        Weekly staffing demand per service recipient · {currentTenant?.name || 'All'}
                    </p>
                </div>
                <TableSkeleton columns={6} rows={8} message="Loading staff demand data…" />
            </div>
        );
    }

    const totalRecipients = totalsPerCustomer.length;
    const totalSN  = summaryTotals.snDay  + summaryTotals.snNight;
    const totalENA = summaryTotals.enaDay + summaryTotals.enaNight;
    const totalCW  = summaryTotals.cwDay  + summaryTotals.cwNight;

    return (
        <div style={pageWrap}>

            {/* ── Hero (title + recipient count) ────────────────────────────── */}
            <div style={{ ...heroStyle, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-end', gap: 16 }}>
                <div>
                    <h2 style={heroTitle}>Staff Demand Report</h2>
                    <p style={heroSubtitle}>
                        Weekly staffing demand per service recipient · {currentTenant?.name || 'All'}
                    </p>
                </div>
                <div style={{ background: 'var(--theme-surface-muted)', border: '1px solid var(--theme-border)', borderRadius: 10, padding: '10px 16px', textAlign: 'center', minWidth: 76 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.25rem', lineHeight: 1, color: 'var(--theme-accent)' }}>{totalRecipients}</div>
                    <div style={{ color: 'var(--theme-text-secondary)', fontSize: '0.72rem', marginTop: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recipients</div>
                </div>
            </div>

            {/* ── Toolbar (site + recipient search) ─────────────────────────── */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 20, width: isMobile ? '100%' : 'auto' }}>
                {/* Site selector */}
                <label style={filterFieldStyle}>
                    <FaBuilding color="var(--theme-accent)" size={13} />
                    <select
                        value={selectedSiteId}
                        onChange={e => setSelectedSiteId(e.target.value)}
                        style={{ ...filterInputStyle, cursor: 'pointer', width: '100%' }}
                    >
                        <option value="">All Sites</option>
                        {tenantSites.map(s => (
                            <option key={s.siteId} value={s.siteId}>
                                {s.siteName}
                            </option>
                        ))}
                    </select>
                </label>

                {/* Recipient search */}
                <label style={filterFieldStyle}>
                    <FaSearch color="var(--theme-accent)" size={13} />
                    <input
                        type="text"
                        placeholder="Search recipients…"
                        value={recipientSearch}
                        onChange={e => setRecipientSearch(e.target.value)}
                        style={{ ...filterInputStyle, width: isMobile ? '100%' : 180 }}
                    />
                </label>
            </div>

            {/* ── Metric Cards ─────────────────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
                {[
                    { icon: <FaUsers size={22} />, value: totalRecipients, label: 'Service Recipients', unit: '', color: COLORS.snDay },
                    { icon: <span style={{ fontSize: 20, lineHeight: 1 }}>🏥</span>, value: totalSN.toFixed(1), label: `Total ${getAbbreviation('SN')} Hrs/wk`, unit: 'h', color: COLORS.enaDay },
                    { icon: <FaUserNurse size={22} />, value: totalENA.toFixed(1), label: `Total ${getAbbreviation('ENA')} Hrs/wk`, unit: 'h', color: COLORS.amber },
                    { icon: <FaHome size={22} />, value: totalCW.toFixed(1), label: `Total ${getAbbreviation('CW')} Hrs/wk`, unit: 'h', color: COLORS.cwDay },
                ].map((card, i) => (
                    <div key={i} style={{
                        ...cardStyle,
                        padding: isMobile ? '16px 14px' : '22px 20px 18px',
                        borderTop: `4px solid ${card.color}`,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 5,
                    }}>
                        <div style={{ color: card.color, opacity: 0.85 }}>{card.icon}</div>
                        <div style={{ fontSize: isMobile ? '1.6rem' : '2.1rem', fontWeight: 800, color: 'var(--theme-text)', lineHeight: 1 }}>
                            {card.value}<span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--theme-text-secondary)' }}>{card.unit}</span>
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-secondary)', fontWeight: 500 }}>
                            {card.label}
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Chart + Staff Requirements ────────────────────────────────── */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16, marginBottom: 20 }}>

                {/* Hours Distribution Chart */}
                <div style={{ ...cardStyle, padding: 24 }}>
                    <div style={{ marginBottom: 16 }}>
                        <h5 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>Hours Distribution</h5>
                        <p style={{ margin: '3px 0 0', fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>Total weekly hours by care category</p>
                    </div>
                    <div style={{ height: 260 }}>
                        <Bar
                            data={chartData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                indexAxis: 'y',
                                plugins: {
                                    legend: { display: false },
                                    tooltip: {
                                        callbacks: {
                                            label: ctx => ` ${ctx.parsed.x.toFixed(1)} hrs/wk`,
                                        },
                                    },
                                },
                                scales: {
                                    x: {
                                        grid: { color: '#f3f4f6' },
                                        ticks: { color: COLORS.textMuted, font: { size: 11 } },
                                    },
                                    y: {
                                        grid: { display: false },
                                        ticks: { color: COLORS.dark, font: { weight: '600', size: 12 } },
                                    },
                                },
                            }}
                        />
                    </div>
                </div>

                {/* Staff Requirements Table */}
                <div style={{ ...cardStyle, padding: 24 }}>
                    <div style={{ marginBottom: 12 }}>
                        <h5 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>Staff Requirements</h5>
                        <p style={{ margin: '3px 0 8px', fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>Calculated minimum staff per category</p>

                        {/* Modifier badges — values read from MODIFIERS constant so they stay in sync with calculations */}
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {[
                                { label: `Statutory ${Math.round(MODIFIERS.statutory * 100)}%`, bg: '#eff6ff', color: '#3b82f6' },
                                { label: `Admin SN/ENA ${Math.round(MODIFIERS.adminSNENA * 100)}% · CW ${Math.round(MODIFIERS.adminCW * 100)}%`, bg: '#f5f3ff', color: '#8b5cf6' },
                                { label: `Efficiency ${Math.round(MODIFIERS.efficiency * 100)}%`, bg: '#f0fdf4', color: '#10b981' },
                            ].map((b, i) => (
                                <span key={i} style={{ background: b.bg, color: b.color, padding: '3px 9px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
                                    {b.label}
                                </span>
                            ))}
                        </div>
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                        <thead>
                            <tr>
                                {['Category', 'Hrs/wk', 'Demand', 'Min', 'Required'].map((h, i) => (
                                    <th key={i} style={{
                                        background: 'var(--theme-table-header-bg)', color: 'var(--theme-table-header-text)', padding: '9px 10px',
                                        textAlign: i === 0 ? 'left' : 'center',
                                        fontWeight: 600,
                                        borderRadius: i === 0 ? '6px 0 0 6px' : i === 4 ? '0 6px 6px 0' : 0,
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {staffRows.map((row, i) => {
                                const required = Math.max(Math.ceil(row.calc), row.min);
                                return (
                                    <tr key={row.label} style={{ background: 'var(--theme-table-surface)', borderBottom: '1px solid var(--theme-table-border)' }}>
                                        <td style={{ padding: '9px 10px', fontWeight: 600, color: 'var(--theme-table-text)' }}>
                                            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: row.color, marginRight: 7, verticalAlign: 'middle' }} />
                                            {row.label}
                                        </td>
                                        <td style={{ padding: '9px 8px', textAlign: 'center', color: 'var(--theme-table-text-secondary)' }}>{row.hours.toFixed(1)}</td>
                                        <td style={{ padding: '9px 8px', textAlign: 'center', color: 'var(--theme-table-text-secondary)' }}>{row.calc.toFixed(2)}</td>
                                        <td style={{ padding: '9px 8px', textAlign: 'center', color: 'var(--theme-table-text-secondary)' }}>{row.min}</td>
                                        <td style={{ padding: '9px 8px', textAlign: 'center', fontWeight: 700, color: 'var(--theme-table-text)', fontSize: '0.95rem' }}>{required}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Per-Recipient Detail Table ──────────────────────────────── */}
            <div style={{ ...cardStyle, overflow: 'hidden' }}>
                {/* Table header bar */}
                <div style={{
                    padding: isMobile ? '14px 16px' : '18px 24px',
                    borderBottom: '1px solid var(--theme-border)',
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: isMobile ? 8 : 0,
                }}>
                    <div>
                        <h5 style={{ margin: 0, fontWeight: 700, color: 'var(--theme-text)' }}>Service Recipients Detail</h5>
                        <p style={{ margin: '3px 0 0', fontSize: '0.82rem', color: 'var(--theme-text-secondary)' }}>
                            {displayTotals.length} of {totalsPerCustomer.length} recipient{totalsPerCustomer.length !== 1 ? 's' : ''}
                            {selectedSiteId ? ` · filtered by site` : ''}
                        </p>
                    </div>
                    {/* Colour legend */}
                    <div style={{ display: 'flex', gap: 14, fontSize: '0.78rem', color: 'var(--theme-text-secondary)' }}>
                        {[['Service Plan', COLORS.snDay], ['Quotation', COLORS.enaDay]].map(([label, color]) => (
                            <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                                <span style={{ width: 10, height: 10, borderRadius: 2, background: color, display: 'inline-block' }} />
                                {label}
                            </span>
                        ))}
                    </div>
                </div>

                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                        <thead>
                            {/* Row 1: group headers */}
                            <tr style={{ background: 'var(--theme-table-header-bg)' }}>
                                <th style={{ ...thBase, width: 36 }}>#</th>
                                <th
                                    style={{ ...thBase, cursor: 'pointer', minWidth: 140 }}
                                    onClick={() => requestSort('customer_name')}
                                >
                                    Service Recipient {sortIcon('customer_name')}
                                </th>
                                <th colSpan={6} style={{ ...thBase, textAlign: 'center' }}>Service Plan Hours</th>
                                <th colSpan={6} style={{ ...thBase, textAlign: 'center', borderLeft: `2px solid var(--theme-table-border-strong)`, borderRight: `2px solid var(--theme-table-border-strong)` }}>Quotation Hours</th>
                                <th colSpan={4} style={{ ...thBase, textAlign: 'center' }}>Details</th>
                            </tr>
                            {/* Row 2: sub-group headers */}
                            <tr style={{ background: 'var(--theme-table-site-header-start)' }}>
                                <th style={{ ...thBase, fontSize: '0.76rem', color: 'var(--theme-table-header-text)' }} />
                                <th style={{ ...thBase, fontSize: '0.76rem', color: 'var(--theme-table-header-text)' }} />
                                {[
                                    // [code, colSpan, borderLeft, borderRight]
                                    ['SN', 2, '', ''],
                                    ['ENA', 2, '', ''],
                                    ['CW', 2, '', ''],
                                    ['SN', 2, `2px solid var(--theme-table-border-strong)`, ''],
                                    ['ENA', 2, '', ''],
                                    ['CW', 2, '', `2px solid var(--theme-table-border-strong)`],
                                ].map(([code, span, borderLeft, borderRight], i) => (
                                    <th key={i} colSpan={span} style={{
                                        ...thBase,
                                        fontSize: '0.76rem',
                                        color: 'var(--theme-table-header-text)',
                                        textAlign: 'center',
                                        ...(borderLeft ? { borderLeft } : {}),
                                        ...(borderRight ? { borderRight } : {}),
                                    }}>
                                        <StaffTypeAbbr
                                            abbreviation={getAbbreviation(code)}
                                            displayName={getDisplayName(code)}
                                            suffix="Hours"
                                            maxWidth="3.25rem"
                                        />
                                    </th>
                                ))}
                                {['Plan ID', 'Quote ID', '% Diff', 'Discount'].map(h => (
                                    <th key={h} style={{ ...thBase, fontSize: '0.76rem', color: 'var(--theme-table-header-text)', textAlign: 'center' }}>{h}</th>
                                ))}
                            </tr>
                            {/* Row 3: Day/Night */}
                            <tr style={{ background: 'var(--theme-table-site-header-end)' }}>
                                <th style={{ ...thBase, fontSize: '0.72rem', color: 'var(--theme-table-header-text)' }} />
                                <th style={{ ...thBase, fontSize: '0.72rem', color: 'var(--theme-table-header-text)' }} />
                                {['Day','Night','Day','Night','Day','Night','Day','Night','Day','Night','Day','Night'].map((d, i) => (
                                    <th key={i} style={{
                                        ...thBase, fontSize: '0.72rem', color: 'var(--theme-table-header-text)', textAlign: 'center',
                                        ...(i === 6 ? { borderLeft: `2px solid var(--theme-table-border-strong)` } : {}),
                                        ...(i === 11 ? { borderRight: `2px solid var(--theme-table-border-strong)` } : {}),
                                    }}>{d}</th>
                                ))}
                                <th style={{ ...thBase, fontSize: '0.72rem', color: 'var(--theme-table-header-text)' }} />
                                <th style={{ ...thBase, fontSize: '0.72rem', color: 'var(--theme-table-header-text)' }} />
                                <th style={{ ...thBase, fontSize: '0.72rem', color: 'var(--theme-table-header-text)' }} />
                                <th style={{ ...thBase, fontSize: '0.72rem', color: 'var(--theme-table-header-text)' }} />
                            </tr>
                        </thead>
                        <tbody>
                            {displayTotals.length === 0 ? (
                                <tr>
                                    <td colSpan={18} style={{ textAlign: 'center', padding: '32px', color: 'var(--theme-table-text-secondary)', fontStyle: 'italic' }}>
                                        {recipientSearch ? `No recipients match "${recipientSearch}"` : 'No service recipients found.'}
                                    </td>
                                </tr>
                            ) : (
                                displayTotals.map((customer, index) => {
                                    const q = quotationTotalsPerCustomer.find(x =>
                                        (customer.customer_id != null && x.customer_id != null)
                                            ? String(x.customer_id) === String(customer.customer_id)
                                            : x.customer_name === customer.customer_name
                                    );
                                    const cpTotal = hoursSum(customer, HOUR_KEYS);
                                    const qTotal  = hoursSum(q, HOUR_KEYS);
                                    const pctDiff = cpTotal > 0 ? ((qTotal - cpTotal) / cpTotal) * 100 : 0;
                                    const diffColor = !q ? '#9ca3af' : pctDiff === -100 ? '#374151' : pctDiff > 0 ? '#ef4444' : '#10b981';

                                    const tdNum = (val) => (
                                        <td style={{ padding: '8px 7px', textAlign: 'center', color: 'var(--theme-table-text)' }}>
                                            {(val || 0).toFixed(2)}
                                        </td>
                                    );

                                    return (
                                        <tr key={index} style={{ background: 'var(--theme-table-surface)', borderBottom: '1px solid var(--theme-table-border)' }}>
                                            <td style={{ padding: '8px 7px', color: 'var(--theme-table-text-secondary)', textAlign: 'center' }}>{index + 1}</td>
                                            <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--theme-table-text)' }}>{customer.customer_name}</td>

                                            {/* Service Plan Hours */}
                                            {tdNum(customer.sn_hours_day)}
                                            {tdNum(customer.sn_hours_night)}
                                            {tdNum(customer.ena_hours_day)}
                                            {tdNum(customer.ena_hours_night)}
                                            {tdNum(customer.cw_hours_day)}
                                            {tdNum(customer.cw_hours_night)}

                                            {/* Quotation Hours */}
                                            <td style={{ padding: '8px 7px', textAlign: 'center', color: 'var(--theme-table-text)', borderLeft: `2px solid var(--theme-table-border-strong)` }}>{(q?.sn_hours_day || 0).toFixed(2)}</td>
                                            {tdNum(q?.sn_hours_night)}
                                            {tdNum(q?.ena_hours_day)}
                                            {tdNum(q?.ena_hours_night)}
                                            {tdNum(q?.cw_hours_day)}
                                            <td style={{ padding: '8px 7px', textAlign: 'center', color: 'var(--theme-table-text)', borderRight: `2px solid var(--theme-table-border-strong)` }}>{(q?.cw_hours_night || 0).toFixed(2)}</td>

                                            {/* Plan ID */}
                                            <td style={{ padding: '8px 7px', textAlign: 'center' }}>
                                                {customer.CarePlanID !== 'N/A' && customer.customer_id ? (
                                                    <Link to={`/careplans/${customer.customer_id}/${customer.CarePlanID}`}
                                                        style={{ color: 'var(--theme-accent)', fontWeight: 600 }}>
                                                        {formatCarePlanId(customer.CarePlanID)}
                                                    </Link>
                                                ) : (
                                                    <span style={{ color: 'var(--theme-table-text-secondary)' }}>{customer.CarePlanID}</span>
                                                )}
                                            </td>

                                            {/* Quote ID */}
                                            <td style={{ padding: '8px 7px', textAlign: 'center' }}>
                                                {q?.quotation_id && q.quotation_id !== 'N/A' && q.customer_id && q.date_created ? (
                                                    <Link to={`/quotation/${q.customer_id}/${q.date_created}/${q.quotation_id}`}
                                                        style={{ color: 'var(--theme-accent)', fontWeight: 600 }}>
                                                        {formatQuotationId(q.quotation_id)}
                                                    </Link>
                                                ) : (
                                                    <span style={{ color: 'var(--theme-table-text-secondary)' }}>{q?.quotation_id || '—'}</span>
                                                )}
                                            </td>

                                            {/* % Difference */}
                                            <td style={{ padding: '8px 7px', textAlign: 'center', fontWeight: 700, color: diffColor }}>
                                                {!q ? '—' : `${pctDiff.toFixed(1)}%`}
                                            </td>

                                            {/* Discount */}
                                            <td style={{ padding: '8px 7px', textAlign: 'center', fontWeight: 600, color: (customer.discount || 0) > 0 ? COLORS.cwDay : 'var(--theme-table-text)' }}>
                                                {parseFloat(customer.discount || 0).toFixed(2)}%
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default StaffDemandReport;
